import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {WishlistStreamItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/stream.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {TrackWishlistPageView} from '@aboutyou/tracking';
import {
  mediaDesktopAndUp,
  mediaLargeScreens,
  mobileOnly,
  Slot,
} from '@aboutyou/ui-components';
import React, {Fragment, memo, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {labelToProductImageType} from '../../../features/ProductStream/helpers';
import {ProductStreamOffsetLimitPagination} from '../../../features/ProductStreamPagination/ProductStreamOffsetLimitPagination';
import {useProductImageType} from '../../../shared-deprecated/useProductImageType';
import {WishlistItemGrid} from '../WishlistItemGrid/WishlistItemGrid';
import {InteractiveWishlistProductTile} from '../WishlistProductTile/InteractiveWishlistProductTile';
import {WishlistProductTile} from '../WishlistProductTile/WishlistProductTile';
import {WishlistEmpty} from './WishlistEmpty';
import {WishlistLoadingComponent} from './WishlistLoadingComponent';
import {WishlistStreamViewProps} from './WishlistPageStreamView';
import {WishlistSubscribeToNewsletterBanner} from './WishlistSubscribeToNewsletterBanner/WishlistSubscribeToNewsletterBanner';

const WishlistProductTileMemo = memo(WishlistProductTile);
const WishlistInteractiveProductTileMemo = memo(InteractiveWishlistProductTile);

const WISHLIST_CURRENTPAGE_STATE_KEY = 'WISHLIST_CURRENTPAGE_STATE_KEY';

const useWishlistSlicedItems = (items: WishlistStreamItem[]) => {
  /**
   * We are using a history page state to preserve the value on e.g history back
   */
  const [currentPage, setCurrentPage] = useHistoryPageState(1, {
    uniqueKey: WISHLIST_CURRENTPAGE_STATE_KEY,
  });
  const currentItems = currentPage * 32;
  return {items: items.slice(0, currentItems - 1), setCurrentPage, currentPage};
};

export const WishlistStreamView: React.FC<WishlistStreamViewProps> = ({
  allItemsTab,
  showConfirmOnDeleteItem,
  onClickAddToBasket,
  onClickSoldoutCta,
  shallowRenderingState,
  gridColumns,
  smallAddToBasketCtaText,
  onSimilarProductsClick,
  interactiveHoverMode,
  productTileGalleryMode,
  isDataFetched,
  isDataFetching,
  enableImageGalleryAnimation,
}) => {
  const items = allItemsTab.items;

  const {
    items: slicedItems,
    setCurrentPage,
    currentPage,
  } = useWishlistSlicedItems(items);

  const [productImageType] = useProductImageType();

  // TODO: Add itemPreferences to the top of the stream and use it for all items since it is the same for all items https://aboutyou.atlassian.net/browse/ASFE-7992
  const itemPreferences: ProductTilePreferences | undefined = useMemo(() => {
    const firstProduct = slicedItems.find(
      item => item.type?.$case === 'product',
    );
    const preferredImgType = labelToProductImageType(productImageType);
    if (
      firstProduct &&
      firstProduct.type?.$case === 'product' &&
      preferredImgType
    ) {
      return {
        ...firstProduct.type?.product.itemPreferences,
        preferredProductImageType: preferredImgType,
      };
    }
    return undefined;
  }, [productImageType, slicedItems]);

  const Tile = interactiveHoverMode
    ? WishlistInteractiveProductTileMemo
    : WishlistProductTileMemo;

  const getFirstTileIndex = () => {
    const firstProductIndex = slicedItems.findIndex(
      item => item.type?.$case === 'product',
    );
    return firstProductIndex === -1 ? 0 : firstProductIndex;
  };

  const firstTileIndex = useMemo(() => {
    return getFirstTileIndex();
  }, [slicedItems]);

  return (
    <Fragment>
      <TrackWishlistPageView
        itemStream={items}
        isDataFetching={isDataFetching}
        isDataFetched={isDataFetched}
      />
      <WishlistItemGrid>
        {slicedItems.map((item, index) => {
          switch (item.type?.$case) {
            case 'product':
              return (
                <Tile
                  smallAddToBasketCtaText={smallAddToBasketCtaText}
                  key={item.type.product.itemKey}
                  streamPosition={item.streamPosition}
                  tile={item.type.product.product}
                  showConfirmOnDeleteItem={showConfirmOnDeleteItem}
                  onClickAddToBasket={onClickAddToBasket}
                  onSoldoutCtaClick={onClickSoldoutCta}
                  shallowRenderingState={shallowRenderingState}
                  gridColumns={gridColumns}
                  itemsPreferences={
                    itemPreferences ?? item.type.product.itemPreferences
                  }
                  onSimilarProductsClick={onSimilarProductsClick}
                  interactiveHoverMode={interactiveHoverMode}
                  productTileGalleryMode={productTileGalleryMode}
                  enableImageGalleryAnimation={
                    index === firstTileIndex && enableImageGalleryAnimation
                  }
                />
              );
            case 'emptyWishlist':
              return (
                <Fragment>
                  {isDataFetching && (
                    <WishlistLoadingComponent onlyPlaceholder />
                  )}

                  {!isDataFetching && (
                    <FullSpanItem
                      withLargeVerticalPadding
                      key={item.streamPosition}
                    >
                      <WishlistEmpty />
                    </FullSpanItem>
                  )}
                </Fragment>
              );
            case 'newsletterBanner':
              return (
                <Slot position={index} Comp="li">
                  <WishlistSubscribeToNewsletterBanner />
                </Slot>
              );
            default:
              return null;
          }
        })}
        <ProductStreamOffsetLimitPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
          Wrapper={ListItem}
        />
      </WishlistItemGrid>
    </Fragment>
  );
};

const ListItem = styled.li``;

const loadMoreComponentDisplayConfig = {
  tilesBeforeFirstDisplay: 10000,
  tilesBeforeConsecutivelyDisplay: 10000,
};

const FullSpanItem = styled.li<{
  padded?: boolean;
  withLargeVerticalPadding?: boolean;
}>`
  margin: ${({padded}) => padded && '20px'} 0px;

  ${({withLargeVerticalPadding}) =>
    withLargeVerticalPadding &&
    css`
      margin: 50px 0px;
    `};

  grid-column: span 2;

  @media ${mediaDesktopAndUp} {
    grid-column: span 3;
  }

  @media ${mediaLargeScreens} {
    grid-column: span 4;
    ${({withLargeVerticalPadding}) =>
      withLargeVerticalPadding &&
      css`
        margin: 100px 0px;
      `};
  }

  ${mobileOnly(css`
    text-align: center;
  `)}
`;
